/**
 * Link previews (SPEC §8.3): POST /api/unfurl; offline pastes store the URL and unfurl on
 * reconnect (queue kept in localStorage).
 */
import type { LinkPreview } from '@terrazzo/core'
import { actions } from '../store/actions'

const KEY = 'tz.unfurlQueue'

function queue(): Array<{ id: string; url: string }> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]')
  } catch {
    return []
  }
}
function save(q: Array<{ id: string; url: string }>) {
  localStorage.setItem(KEY, JSON.stringify(q.slice(-50)))
}

export async function fetchPreview(url: string): Promise<LinkPreview | null> {
  const res = await fetch('/api/unfurl', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ url }),
  })
  if (!res.ok) throw new Error(`unfurl ${res.status}`)
  return (await res.json()) as LinkPreview
}

export async function unfurlInto(id: string, url: string): Promise<void> {
  actions.patch(id, { link: { url } })
  if (!navigator.onLine) {
    save([...queue(), { id, url }])
    return
  }
  try {
    const preview = await fetchPreview(url)
    if (preview && actions.exists(id)) actions.patch(id, { link: { ...preview, url } })
  } catch {
    save([...queue(), { id, url }])
  }
}

export async function drainUnfurlQueue(): Promise<void> {
  const q = queue()
  if (!q.length || !navigator.onLine) return
  save([])
  for (const { id, url } of q) {
    if (!actions.exists(id)) continue
    try {
      const preview = await fetchPreview(url)
      if (preview) actions.patch(id, { link: { ...preview, url } })
    } catch {
      save([...queue(), { id, url }])
    }
  }
}

if (typeof window !== 'undefined') window.addEventListener('online', () => void drainUnfurlQueue())
